/**
 * Recovery-team entity adapter (round 13 D9).
 *
 * P371 modelled the team that restores a service, and the MVB recovery
 * sequence has been bounded by its capacity ever since -- but nothing
 * ever showed one. A team could be created by CSV import or by API and
 * then never looked at, so the import panel's "See the rows you
 * imported" had nowhere to land, and the figure bounding the recovery
 * plan could not be checked by the person accountable for it.
 *
 * Deliberately the NARROW slice P371 built, not a people system:
 * `max_concurrent_restorations` is a capacity bound on a schedule, and
 * `headcount` is context for reading it.
 */
import { useTranslation } from "react-i18next";

import type { AdminColumn, EntityAdapter, FormField } from "./types";

/** NULL capacity means the scheduler treats the team as unbounded,
 * which is a different claim from zero -- and a blank cell reads as a
 * zero, so it is said in a word. From the catalogue, because a cell a
 * translator cannot reach is a cell in one language. */
function CapacityCell({ team }: { team: RecoveryTeam }) {
  const { t } = useTranslation();
  return (
    <span
      data-testid={`recovery-team-capacity-${team.code}`}
      className="text-xs tabular-nums text-slate-600"
    >
      {team.max_concurrent_restorations ??
        t("modules.foundation.admin.recoveryTeams.unbounded")}
    </span>
  );
}

export interface RecoveryTeam {
  id: string;
  code: string;
  name: string;
  description: string | null;
  headcount: number | null;
  max_concurrent_restorations: number | null;
  contact_email: string | null;
  created_at: string;
  updated_at: string;
}

export interface RecoveryTeamIn {
  code: string;
  name: string;
  description: string | null;
  headcount: number | null;
  max_concurrent_restorations: number | null;
  contact_email: string | null;
}

export type RecoveryTeamPatch = Partial<RecoveryTeamIn>;

const columns: AdminColumn<RecoveryTeam>[] = [
  {
    key: "code",
    label: "Code",
    sortValue: (r) => r.code,
    render: (r) => <span className="font-mono text-xs text-slate-700">{r.code}</span>,
  },
  {
    key: "name",
    label: "Name",
    sortValue: (r) => r.name,
    render: (r) => <span className="font-medium text-slate-900">{r.name}</span>,
  },
  {
    key: "headcount",
    label: "People",
    align: "right",
    sortValue: (r) => r.headcount ?? -1,
    render: (r) => (
      <span className="text-xs tabular-nums text-slate-600">{r.headcount ?? "--"}</span>
    ),
  },
  {
    key: "max_concurrent_restorations",
    label: "At once",
    align: "right",
    sortValue: (r) => r.max_concurrent_restorations ?? -1,
    // The figure that BOUNDS the MVB recovery sequence.
    render: (r) => <CapacityCell team={r} />,
  },
  {
    key: "contact_email",
    label: "Contact",
    render: (r) => (
      <span className="text-xs text-slate-600">{r.contact_email ?? ""}</span>
    ),
  },
];

const formFields: FormField<RecoveryTeamIn>[] = [
  { kind: "text", key: "code", label: "Code", required: true, maxLength: 64 },
  { kind: "text", key: "name", label: "Name", required: true, maxLength: 255 },
  { kind: "textarea", key: "description", label: "Description", rows: 3 },
  { kind: "number", key: "headcount", label: "Headcount", min: 0, max: 100000 },
  {
    kind: "number",
    key: "max_concurrent_restorations",
    label: "Restorations at once (blank = unbounded)",
    min: 1,
    max: 1000,
  },
  {
    kind: "text",
    key: "contact_email",
    label: "Contact email",
    maxLength: 255,
  },
];

export const recoveryTeamAdapter: EntityAdapter<
  RecoveryTeam,
  RecoveryTeamIn,
  RecoveryTeamPatch
> = {
  i18nPrefix: "modules.foundation.admin.recoveryTeams",
  importKind: "recovery_team",
  endpoint: "/v1/admin/foundation/recovery-teams",
  queryKey: ["foundation-admin", "recovery-teams"] as const,
  columns,
  defaultSort: { key: "code", dir: "asc" },
  rowKey: (r) => r.id,
  rowSearchHaystack: (r) =>
    `${r.code} ${r.name} ${r.description ?? ""} ${r.contact_email ?? ""}`,
  formFields,
  formInitial: () => ({
    code: "",
    name: "",
    description: null,
    headcount: null,
    max_concurrent_restorations: null,
    contact_email: null,
  }),
  rowToPatch: (r) => ({
    code: r.code,
    name: r.name,
    description: r.description,
    headcount: r.headcount,
    max_concurrent_restorations: r.max_concurrent_restorations,
    contact_email: r.contact_email,
  }),
};
